import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/user'
import { useUiStore } from '@/stores/ui'
import { useAuthAPI } from '@/services/auth/authAPI'
import { authActions } from '@/services/auth/actions/authActions'
import { useUi } from '@/composables/useUi'

import type { AppPermissions } from '@/types/enums/permissions/permissions'
import type { UpdateAuthenticatedUserProfileRequest } from '@/types/auth/Requests/UpdateAuthenticatedUserRequest'
import type {
  UpdateAuthenticatedUserPasswordRequest,
} from '@/types/auth/Requests/UpdateAuthenticatedUserPasswordRequest'
import type { AttemptLoginRequest } from '@/types/auth/Requests/AttemptLoginRequest'
import type { UpdateAuthenticatedUserPasswordDTO } from '@/types/auth/DTOs/UpdateAuthenticatedUserPasswordDTO'
import type { AuthenticatedUserProfile } from '@/types/auth/AuthenticatedUserProfile'
import type { AuthenticatedUserDepartment } from '@/shared/model/types/AuthenticatedUser/AuthenticatedUserDepartment'
import type { UpdateAuthenticatedUserProfileDTO } from '@/services/auth/DTOs/authDTOs'

export const useAuthService = () => {
  const {
    handleActionAttempt,
    handleActionError,
  } = useUi()
  const {
    storeRemoveFromActionsInProgress,
  } = useUiStore()
  const {
    apiGetCSRFCookie,
    apiAttemptLogin,
    apiLogout,
    apiGetAuthenticatedUser,
    apiGetAuthenticatedUserProfile,
    apiUpdateAuthenticatedUserPassword,
    apiUpdateAuthenticatedUserProfile,
  } = useAuthAPI()
  const { storeSetAuthenticatedUser } = useUserStore()
  const {
    storeAuthenticatedUser,
  } = storeToRefs(useUserStore())

  const attemptLogin = async (dto: AttemptLoginRequest) => {
    handleActionAttempt(authActions.attemptLogin)
    try {
      await apiGetCSRFCookie()
      const attemptLoginRequest: AttemptLoginRequest = {
        ...dto,
      }
      const rsp = await apiAttemptLogin(attemptLoginRequest)
      if (rsp) {
        storeSetAuthenticatedUser(rsp.data.authenticatedUser)
        return rsp.data.authenticatedUser
      }
    }
    catch (e) {
      handleActionError(authActions.attemptLogin, e)
      return e
    }
  }

  const logout = async () => {
    handleActionAttempt(authActions.logout)
    try {
      await apiLogout()
      location.reload()
    }
    catch (e) {
      handleActionError(authActions.logout, e)
    }
  }

  const authenticatedUserHasPermission = (
    permission: AppPermissions,
  ): boolean => {
    return storeAuthenticatedUser.value?.permissions.some(userPermission => userPermission.slug === permission) ?? false
  }

  const authenticatedUserHasSomePermissions = (
    permissions: AppPermissions[],
  ): boolean => {
    return permissions.some(permission => authenticatedUserHasPermission(permission))
  }

  const fetchAndSetStoreAuthenticatedUser = async (): Promise<void> => {
    try {
      const rsp = await apiGetAuthenticatedUser()
      storeSetAuthenticatedUser(rsp.data.authenticatedUser)
    }
    catch (_e) {
      storeSetAuthenticatedUser(null)
    }
  }

  const updateAuthenticatedUserProfile = async (dto: UpdateAuthenticatedUserProfileDTO) => {
    handleActionAttempt(authActions.updateAuthenticatedUserProfile)
    try {
      const updateAuthenticatedUserProfileRequest: UpdateAuthenticatedUserProfileRequest = {
        avatarFileId: dto.avatar ? dto.avatar.id : null,
        firstName: dto.firstName,
        lastName: dto.lastName,
        email: dto.email,
        contactNumber: dto.contactNumber,
        kennitala: dto.kennitala,
        bankAccountNumber: dto.bankAccountNumber,
        nextOfKinName: dto.nextOfKinName,
        nextOfKinContactNumber: dto.nextOfKinContactNumber,
      }

      const rsp = await apiUpdateAuthenticatedUserProfile(updateAuthenticatedUserProfileRequest)
      storeRemoveFromActionsInProgress(authActions.updateAuthenticatedUserProfile)
      return rsp
    }
    catch (e) {
      handleActionError(authActions.updateAuthenticatedUserProfile, e)
    }
  }

  const updateAuthenticatedUserPassword = async (dto: UpdateAuthenticatedUserPasswordDTO) => {
    handleActionAttempt(authActions.updateAuthenticatedUserPassword)
    try {
      const updateAuthenticatedUserPasswordRequest: UpdateAuthenticatedUserPasswordRequest = {
        ...dto,
      }
      const rsp = await apiUpdateAuthenticatedUserPassword(updateAuthenticatedUserPasswordRequest)
      storeRemoveFromActionsInProgress(authActions.updateAuthenticatedUserPassword)
      return rsp
    }
    catch (e) {
      handleActionError(authActions.updateAuthenticatedUserPassword, e)
    }
  }

  const fetchAuthenticatedUserProfile = async (): Promise<AuthenticatedUserProfile | undefined> => {
    handleActionAttempt(authActions.getAuthenticatedUserProfile)
    try {
      const rsp = await apiGetAuthenticatedUserProfile()
      if (rsp) {
        storeRemoveFromActionsInProgress(authActions.getAuthenticatedUserProfile)
        return rsp.data.authenticatedUserProfile
      }
    }
    catch (e) {
      handleActionError(authActions.getAuthenticatedUserProfile, e)
    }
  }

  const authenticatedUserPrimaryDepartment = (): AuthenticatedUserDepartment | null => {
    const { storeAuthenticatedUser } = storeToRefs(useUserStore())
    if (!storeAuthenticatedUser.value || !storeAuthenticatedUser.value.departments) {
      return null
    }

    return storeAuthenticatedUser.value.departments.find(department => department.isPrimary)
      ?? null
  }

  return {
    attemptLogin,
    logout,
    authenticatedUserHasPermission,
    authenticatedUserHasSomePermissions,
    fetchAndSetStoreAuthenticatedUser,
    updateAuthenticatedUserProfile,
    updateAuthenticatedUserPassword,
    fetchAuthenticatedUserProfile,
    authenticatedUserPrimaryDepartment,
  }
}
